import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { enqueue } from "../../lib/offlineQueue/queue";

import { type FavoriteListItem, listFavorites } from "./api";

const FAVORITES_KEY = ["favorites"] as const;

export function useFavorites(enabled: boolean) {
  return useQuery<FavoriteListItem[]>({
    queryKey: FAVORITES_KEY,
    queryFn: listFavorites,
    enabled,
    staleTime: 30_000,
  });
}

// Optimistic toggle — updates the cache immediately so HeartButton flips
// without waiting on the round-trip; rolls back on failure. The toggle
// goes through the offline queue so anonymous-allowed flows persist
// across reconnects.
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { stationId: string; favorite: boolean },
    { previous: FavoriteListItem[] }
  >({
    mutationFn: async ({ stationId, favorite }) => {
      await enqueue({
        endpoint: favorite ? "POST /favorites" : "DELETE /favorites",
        body: { station_id: stationId },
      });
    },
    onMutate: async ({ stationId, favorite }) => {
      await qc.cancelQueries({ queryKey: FAVORITES_KEY });
      // Seed the cache so the rollback always has something to restore
      // and the optimistic write is visible even when the user clicked
      // before the first fetch lands.
      const previous = qc.getQueryData<FavoriteListItem[]>(FAVORITES_KEY) ?? [];
      if (favorite) {
        const exists = previous.some((f) => f.stationId === stationId);
        if (!exists) {
          qc.setQueryData<FavoriteListItem[]>(FAVORITES_KEY, [
            ...previous,
            { stationId, savedAt: new Date().toISOString() },
          ]);
        }
      } else {
        qc.setQueryData<FavoriteListItem[]>(
          FAVORITES_KEY,
          previous.filter((f) => f.stationId !== stationId),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) qc.setQueryData(FAVORITES_KEY, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: FAVORITES_KEY });
    },
  });
}
